import * as React from "react";
import {GridColDef} from "@mui/x-data-grid";
import {formatTime, stringAvatar} from "@/helper/method";
import {BtnType, ProTable} from "@/widget/ProTable/ProTable";
import {ProjectPageVO, useQueryProjectPage} from "@/service/project.s";
import {WriteProject} from "@/pages/Project/WriteProject";
import {useParams} from "react-router-dom";
import {useEffect, useMemo} from "react";
import {ViewProject} from "@/pages/Project/ViewProject";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {ButtonGroup} from "@mui/material";
import Button from "@mui/material/Button";
import {NavigateBefore, NavigateNext} from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import {useGlobalActions} from "@/store/global.c";
import {DescriptionOutlined} from '@mui/icons-material';
import {useClaimProduct, useTransferProduct} from "@/service/contract.s";
import {defaultPageParams, permScope, roleAlias} from "@/helper/constant";
import {WriteStatus} from "@/widget/WriteStatus/ReviewStatus";
import {TransferForm, WriteTransferForm} from "@/widget/TransferForm/TransferForm";
import {useRoleUsers} from "@/store/user.c";
import {useAuthInfo} from "@/store/account.c";
import {SellerAvatar} from "@/widget/SellerAvatar/SellerAvatar";

const projectCode: { [key: string]: string } = {
    'all': "all_project",
    "unclaim": "unclaim_project",
    "duty": "duty_project",
    "defend": "defend_project",
}

const projectStatus: { [key: string]: number } = {
    'all': 0,
    "unclaim": 5,
    "duty": 6,
    "defend": 8,
}

const initPage = (page: string): [number, string] => {
    return [projectStatus[page], projectCode[page]]
}

export const ProjectList = () => {
    const {mutateAsync: transferProductMutate} = useTransferProduct()
    const {setToast, setDialogModal} = useGlobalActions()
    const users = useRoleUsers(["declarer", "financer"])
    const {mutateAsync: claimProductMutate} = useClaimProduct()
    const {page} = useParams()
    const [writeStatus, code] = initPage(page!)
    const {
        mutate: projectPageMutate,
        data: projectPageData,
        isPending: projectPagePending,
        isIdle
    } = useQueryProjectPage(writeStatus)
    const {role: {dataScope, roleAlias: alias}} = useAuthInfo()

    const allColumns: GridColDef<ProjectPageVO>[] = [
        {
            field: "category",
            headerName: "政策项目名称",
            headerAlign: "left",
            align: "left", flex: 0.7
        },
        {field: "customerName", headerName: "客户名称", flex: 0.7},
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "projectStatus",
            headerName: "申报状态",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => value || '未申报'
        },
        {
            field: "publicityDate",
            headerName: "公示日期",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => value && formatTime("D", value) || '-'
        },
        // {
        //     field: "endDate",
        //     headerName: "官方启止时间",
        //     headerAlign: "center",
        //     align: "center",
        //     valueFormatter: (value) => formatTime("D", value),
        // },
        {
            field: "writer",
            headerName: "撰稿人",
            headerAlign: "center",
            align: "center",
            valueFormatter: value => value || "-"
        },
        {
            field: "writeStatus",
            headerName: "撰稿状态",
            flex: 0.4,
            headerAlign: "center",
            align: "center",
            display: "flex",
            renderCell: ({row}) => {
                return <WriteStatus writeStatus={row?.writeStatus} writeStatusId={row?.writeStatusId}/>
            }
        },
        // {
        //     field: "nodeNames",
        //     headerName: "审核状态",
        //     headerAlign: "center",
        //     align: "center",
        //     renderCell: ({row}) => <ReviewStatus processorIds={row.processorIds} authId={id} writeStatusId={row.writeStatusId} auditStatus={row.auditStatus}
        //                                          nodeNames={row.nodeNames}/>,
        // },
        {
            field: "receiveTime",
            headerName: "领取时间",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => formatTime("D", value),
        },
    ];

    const defendColumns: GridColDef<ProjectPageVO>[] = [
        {
            field: "category",
            headerName: "政策项目名称",
            headerAlign: "left",
            align: "left", flex: 0.3
        },
        {
            field: "customerName",
            headerName: "客户名称",
            flex: 0.3
        },
        {
            field: "approvedYear",
            headerName: "获批年度",
            headerAlign: "center",
            flex: 0.2,
            align: "center",
            renderCell: ({row}) => <Chip variant={'outlined'} size={'small'} color={'warning'} label={formatTime("Y", row.approvedYear)}/>
        },
        {
            field: "projectStatus",
            headerName: "申报状态",
            headerAlign: "center",
            align: "center",
            flex: 0.2,
            display: "flex",
            renderCell: ({row}) => <Typography color={'info'}>{row.projectStatus}</Typography>
        },
        {
            field: "subsidyAmount",
            headerName: "已或补贴",
            headerAlign: "center",
            align: "center",
            flex: 0.2,
        },
        {
            field: "section",
            headerName: "受理部门",
            headerAlign: "center",
            align: "center",
            flex: 0.2,
            valueFormatter: value => value || "-"
        },
        {
            field: "validDate",
            headerName: "资质有效截止时间",
            headerAlign: "center",
            flex: 0.3,
            align: "center",
            valueFormatter: (value) => formatTime("D", value),
        },
        {
            field: "publicityDate",
            headerName: "通过时间",
            headerAlign: "center",
            flex: 0.25,
            align: "center",
            valueFormatter: (value) => formatTime("D", value),
        },
    ];

    const dutyColumns: GridColDef<ProjectPageVO>[] = [
        {
            field: "category",
            headerName: "政策项目名称",
            headerAlign: "left",
            align: "left", flex: 0.6
        },
        {
            field: "customerName",
            headerName: "客户名称",
            flex: 0.5
        },
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "projectStatus",
            headerName: "申报状态",
            flex: 0.4,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "publicityDate",
            headerName: "公示日期",
            headerAlign: "center",
            align: "center",
            flex: 0.4,
            valueFormatter: value => formatTime("D", value) || "-",
        },
        {
            field: "endDate",
            headerName: "官方启止时间",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            valueFormatter: (value) => formatTime("D", value),
        },
        {
            field: "writeStatus",
            headerName: "撰稿状态",
            flex: 0.4,
            headerAlign: "center",
            align: "center",
            display: "flex",
            renderCell: ({row}) => {
                return <WriteStatus writeStatus={row?.writeStatus} writeStatusId={row?.writeStatusId}/>
            }
        },
        {
            field: "receiveTime",
            headerName: "领取时间",
            headerAlign: "center",
            align: "center",
            flex: 0.38,
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    const claimColumns: GridColDef<ProjectPageVO>[] = [
        {
            field: "kind",
            headerName: "服务名称",
            headerAlign: "left",
            align: "left", flex: 0.6,
            valueFormatter: value => value || "-"
        },
        {
            field: "category",
            headerName: "政策项目名称",
            headerAlign: "left",
            align: "left", flex: 0.8
        },
        {
            field: "customerName",
            headerName: "客户名称",
            flex: 0.8
        },
        {
            field: "seller",
            headerName: "业务人员",
            headerAlign: "center",
            align: "center",
            display: "flex",
            valueGetter: (_, row) => ({creator: row.creator, seller: row.seller}),
            renderCell: SellerAvatar
        },
        {
            field: "price",
            headerName: "价格",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
        },
        {
            field: "insideDeadline",
            headerName: "内部期限",
            flex: 0.6,
            valueFormatter: (value) => formatTime("D", value),
        },
        {
            field: "assigner",
            headerName: "分配人",
            valueFormatter: (value) => value || '-'
        },
        {
            field: "assignTime",
            headerName: "分配时间",
            headerAlign: "center",
            align: "center",
            flex: 0.6,
            valueFormatter: (value) => formatTime("D", value),
        },
    ]

    useEffect(() => {
        if (isIdle == false) {
            projectPageMutate({current: 1, size: 20})
        }
    }, [writeStatus]);


    const columns = useMemo(() => {
        switch (writeStatus) {
            case 0:
                return allColumns
            case 8:
                return defendColumns
            case 5:
                return claimColumns
            case 6:
                return dutyColumns
        }
        return []
    }, [writeStatus]);


    const submitTransferCallback = async (productTypeId: number, keys: number[], onSearch?: (params?: any) => void, values?: WriteTransferForm) => {
        const result = await transferProductMutate({ids: keys, productTypeId, writer: values!.username!, writerId: values!.userId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setToast({message: result.msg, open: true, severity: 'success'})
            setDialogModal({open: false})
        } else {
            setToast({message: result.msg, open: true, severity: 'error'})
        }
    }

    const btnCallback = async ({btn, keys, onSearch, rows}: BtnType<ProjectPageVO>) => {
        if (btn.alias == "transfer") {
            if (keys.length <= 10 && keys.length > 0) {
                // TODO
                // const project = find(rows, (row: ProjectPageVO) => row.writerId != authId);
                // if (!isEmpty(project)) {
                //     setToast({message: "选择的记录存在不属于你撰写的项目,请取消不是你撰写的项目再试", open: true, severity: 'warning'})
                //     return
                // }
                const content: string = rows.map((row: ProjectPageVO) => `${row.customerName}:${row.category}`)?.join(",")
                setDialogModal({
                    header: btn.menuName,
                    maxWidth: 'xs',
                    body: <TransferForm content={content} submitCallback={(values: WriteTransferForm) => submitTransferCallback(2, keys, onSearch, values)} users={users}
                                        ids={keys}/>,
                    open: true,
                })
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
        if (btn.alias == "claim") {
            if (keys.length <= 10 && keys.length > 0) {
                const result = await claimProductMutate({ids: keys, productTypeId: 2})
                if (result.code == 0) {
                    onSearch && onSearch(defaultPageParams)
                    setToast({message: result.msg, open: true, severity: 'success'})
                }
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
    }

    return (
        <ProTable<ProjectPageVO>
            loading={projectPagePending}
            columnVisibilityModel={{
                seller: !(alias == roleAlias["marketing"] && dataScope == permScope['self']),
                writer: !(alias == roleAlias["declarer"] && dataScope == permScope['self'])
            }}
            renderDrawer={{view: <ViewProject/>, write: <WriteProject/>}}
            columns={columns}
            btnCallback={btnCallback}
            fieldKey={writeStatus == 4 ? 'contractId' : 'id'}
            drawerProps={{
                view: {
                    custom: true,
                    viewBtnCallback: (row: ProjectPageVO) => {
                        return {
                            title: row.category,
                            businessId: row.contractId,
                            switchButton: <ButtonGroup size={'small'} sx={{
                                "& .MuiButton-startIcon": {marginRight: 0},
                                "& .MuiButton-endIcon": {marginLeft: 0}
                                , "& .MuiButtonGroup-grouped": {minWidth: 20, height: 20, width: 12, bgcolor: '#f2f4fb',}
                            }} variant="outlined">
                                <Button size={'small'} startIcon={<NavigateBefore/>}/>
                                <Button size={'small'} endIcon={<NavigateNext/>}/>
                            </ButtonGroup>,
                            icon: <DescriptionOutlined color={'info'} fontSize={'small'}/>,
                            tags: <Stack direction={'row'} spacing={1.4} alignContent={'center'}>
                                <Chip size={'small'} variant={'outlined'} sx={{height: 16}} color={'warning'}
                                      label={<Typography fontSize={10}>{row.projectStatus || '-'}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'error'}
                                      label={<Typography fontSize={10}>{row.approvedYear && `${formatTime("Y", row.approvedYear)}年度` || "-"}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'success'} label={<Typography fontSize={10}>{row.nodeNames}</Typography>}/>
                            </Stack>, instanceId: row.instanceId
                        }
                    }, bodyWidth: 580, headerHeight: 68, hiddenFooter: true
                }
            }}
            data={projectPageData?.data}
            onSearch={(params) => {
                projectPageMutate(params);
            }}
            code={code}
        />
    );
};
